import { Component } from '@theme/component';
import { ThemeEvents, VariantUpdateEvent, ZoomMediaSelectedEvent } from '@theme/events';

/**
 * A custom element that renders a media gallery.
 *
 * @typedef {object} Refs
 * @property {import('./zoom-dialog').ZoomDialog} [zoomDialogComponent] - The zoom dialog component.
 * @property {import('./slideshow').Slideshow} [slideshow] - The slideshow component.
 * @property {HTMLElement[]} [media] - The media elements.
 *
 * @extends Component<Refs>
 */
export class MediaGallery extends Component {
  #controller = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#controller;
    const target = this.closest('.shopify-section, dialog');

    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    this.slideshow?.addEventListener('slideshow:select', this.#handleCarouselSilentVariantSync, { signal });
    this.refs.zoomDialogComponent?.addEventListener(ThemeEvents.zoomMediaSelected, this.#handleZoomMediaSelected, {
      signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.#controller.abort();
  }

  /**
   * Handles a variant update event by replacing the current media gallery with a new one.
   *
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  #handleVariantUpdate = (event) => {
    const source = event.detail.data.html;

    if (!source) return;
    const newMediaGallery = source.querySelector('media-gallery');

    if (!newMediaGallery) return;

    this.replaceWith(newMediaGallery);
  };

  /**
   * Handles the 'zoom-media:selected' event.
   * @param {ZoomMediaSelectedEvent} event - The zoom-media:selected event.
   */
  #handleZoomMediaSelected = async (event) => {
    this.slideshow?.select(event.detail.index, undefined, { animate: false });
  };

  /**
   * Sync the visible slideshow image to the real variant picker state,
   * without triggering variantChanged() or refetching the section.
   * If the image is not tied to a swatch, clear the swatch selection
   * and disable Add to Cart.
   * @param {CustomEvent} event
   */
  #handleCarouselSilentVariantSync = (event) => {
    const slide = event.detail?.slide;
    if (!slide) return;

    const mediaElement = slide.querySelector('[data-media-id]');
    const mediaId = mediaElement?.dataset?.mediaId;
    if (!mediaId) return;

    const section = this.closest('.shopify-section, dialog');
    if (!section) return;

    const variantPicker = section.querySelector('variant-picker');
    if (!variantPicker) return;

  // Buscamos el input por el media-id en todo el variant-picker, sin importar el fieldset
const matchingInput = variantPicker.querySelector(
  `input[data-option-media-id="${mediaId}"]`
);


    const productForm = section.querySelector('product-form-component');
    const variantIdInput = productForm?.querySelector('input[name="id"]');
    const addToCartComponent = productForm?.querySelector('add-to-cart-component');
    const addToCartButton =
      addToCartComponent?.refs?.addToCartButton || productForm?.querySelector('button[name="add"]');

    if (!matchingInput) {
      if (typeof variantPicker.silentClearSwatchSelection === 'function') {
        variantPicker.silentClearSwatchSelection();
      }

      if (variantIdInput) {
        variantIdInput.value = '';
      }

      if (productForm?.refs?.variantId) {
        productForm.refs.variantId.value = '';
      }

      if (addToCartComponent?.disable) {
        addToCartComponent.disable();
      } else if (addToCartButton) {
        addToCartButton.disabled = true;
      }

      const url = new URL(window.location.href);
      url.searchParams.delete('variant');
      history.replaceState({}, '', url.toString());
      return;
    }

    const result =
      typeof variantPicker.silentSelectOption === 'function'
        ? variantPicker.silentSelectOption(matchingInput)
        : null;

    if (!result?.variantId) return;

    if (variantIdInput) {
      variantIdInput.value = result.variantId;
    }

    if (productForm?.refs?.variantId) {
      productForm.refs.variantId.value = result.variantId;
    }

    if (result.available) {
      if (addToCartComponent?.enable) {
        addToCartComponent.enable();
      } else if (addToCartButton) {
        addToCartButton.disabled = false;
      }
    } else {
      if (addToCartComponent?.disable) {
        addToCartComponent.disable();
      } else if (addToCartButton) {
        addToCartButton.disabled = true;
      }
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', result.variantId);
    history.replaceState({}, '', url.toString());
  };

  /**
   * Zooms the media gallery.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  zoom(index, event) {
    this.refs.zoomDialogComponent?.open(index, event);
  }

  /**
   * Preloads an image.
   * @param {number} index - The index of the media to preload.
   */
  preloadImage(index) {
    const zoomDialogMedia = this.refs.zoomDialogComponent?.refs.media[index];
    if (!zoomDialogMedia) return;

    this.refs.zoomDialogComponent?.loadHighResolutionImage(zoomDialogMedia);
  }

  get slideshow() {
    return this.refs.slideshow;
  }

  get media() {
    return this.refs.media;
  }

  get presentation() {
    return this.dataset.presentation;
  }
}

if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', MediaGallery);
}